import { useMemo, useState } from "react";

import {
	AssignStatus,
	InclusionFilterStatus,
	PriorityFilterStatus,
	SelectType,
	SignalFilterStatus,
} from "@/enum/list.enum";

import { ListPerson } from "../../../schema/list";
import { IFilterWithValues } from "../../FilterModal";
import { ReachOutBulkTarget } from "../../ReachOut/reachOutTarget";

interface UseBulkReachOutParams {
	data?: ListPerson[];
	listId?: number;
	selectedItems: number[];
	selectType: SelectType;
	itemCount?: number;
	filter?: IFilterWithValues;
	inclusionStatus?: InclusionFilterStatus[];
	priorityFilterStatus?: PriorityFilterStatus[];
	signalFilterStatus?: SignalFilterStatus[];
	assignStatus?: AssignStatus;
	/** The table's search term. */
	searchValue?: string;
	/** Every status of the tab the table showed; the Assigned tab stands for several (PREC-4450). */
	assignStatuses?: AssignStatus[];
	/** The reason sub-filters the table showed, "STATUS:filterParam" (PREC-4450). */
	exclusionReasons?: string[];
}

interface BulkReachOut {
	/** The reach-out target for the current selection, or null when nothing is selectable. */
	reachOutBulkTarget: ReachOutBulkTarget | null;
	/** Anchor rect of the footer trigger while the menu is open (null = closed). */
	reachOutAnchor: DOMRect | null;
	openReachOut: (anchorRect: DOMRect) => void;
	closeReachOut: () => void;
}

/**
 * Owns the lead-list bulk "Reach out" wiring: the open/close anchor state and the reach-out target
 * built from the current selection (current page or "all across pages"), the list it belongs to, and
 * a representative seed row for the AI panel's display defaults. Extracted from UnassignedTable so
 * that large file need not grow to host the feature.
 */
export const useBulkReachOut = ({
	data,
	listId,
	selectedItems,
	selectType,
	itemCount,
	filter,
	inclusionStatus,
	priorityFilterStatus,
	signalFilterStatus,
	assignStatus,
	searchValue,
	assignStatuses,
	exclusionReasons,
}: UseBulkReachOutParams): BulkReachOut => {
	const [reachOutAnchor, setReachOutAnchor] = useState<DOMRect | null>(null);

	const reachOutBulkTarget = useMemo<ReachOutBulkTarget | null>(() => {
		const seedRow = data?.find((row) => selectedItems.includes(row.id)) ?? data?.[0];

		if (!listId || !seedRow?.list || selectedItems.length === 0) {
			return null;
		}

		return {
			kind: "bulk",
			listId,
			list: seedRow.list,
			seedRow,
			selectedItems,
			selectedRows: data?.filter((row) => selectedItems.includes(row.id)) ?? [],
			selectType,
			count: selectType === SelectType.ALL ? (itemCount ?? 0) : selectedItems.length,
			filter,
			inclusionFilterStatus: inclusionStatus ?? undefined,
			priorityFilterStatus: priorityFilterStatus ?? undefined,
			signalFilterStatus: signalFilterStatus ?? undefined,
			assignStatus: assignStatus ?? undefined,
			searchValue: searchValue?.trim() || undefined,
			assignStatuses,
			exclusionReasons,
		};
	}, [
		data,
		listId,
		selectedItems,
		selectType,
		itemCount,
		filter,
		inclusionStatus,
		priorityFilterStatus,
		signalFilterStatus,
		assignStatus,
		searchValue,
		assignStatuses,
		exclusionReasons,
	]);

	return {
		reachOutBulkTarget,
		reachOutAnchor,
		openReachOut: setReachOutAnchor,
		closeReachOut: () => setReachOutAnchor(null),
	};
};
